import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  FileSpreadsheet, 
  FileText,
  Upload, 
  Download, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Trash2,
  FolderOpen,
  RefreshCw,
  Clock,
  XCircle,
  Files
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const InvoiceClassifierTab = ({ token }) => {
  const [excelFile, setExcelFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null); // Single ZIP or will be ignored for individual PDFs
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  
  const excelInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    fetchJobHistory();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token]);

  const fetchJobHistory = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/invoice-classifier/jobs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobHistory(response.data.jobs || []);
    } catch (err) {
      console.error('Error fetching job history:', err);
    }
  };

  const pollJobStatus = useCallback(async (jobId) => {
    if (!token || !jobId) return;
    
    try {
      const response = await axios.get(`${API}/invoice-classifier/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const job = response.data;
      
      if (job.status === 'completed') {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setProcessingStage('done');
        setLoading(false);
        setResult({
          jobId: jobId,
          totalPdfs: job.total_pdfs,
          matched: job.matched,
          unmatched: job.unmatched,
          totalExcelRows: job.total_excel_rows || 0,
          greenSkipped: job.green_skipped || 0,
          activeExcelRows: job.active_excel_rows || 0,
          matches: job.matches || [],
          errors: job.errors || [],
          zipFilename: job.zip_filename
        });
        toast.success(`Clasificación completada: ${job.matched} de ${job.total_pdfs} PDFs en el ZIP`);
        fetchJobHistory();
      } else if (job.status === 'error') {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setProcessingStage('');
        setLoading(false);
        setError(job.error || 'Error desconocido');
        setCurrentJobId(null);
        toast.error('Error en la clasificación');
      } else if (job.status === 'extracting') {
        setProcessingStage('extracting');
      } else if (job.status === 'processing') {
        setProcessingStage('processing');
      }
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [token]);

  const handleExcelSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Por favor selecciona un archivo Excel (.xlsx o .xls)');
      return;
    }

    setExcelFile(file);
    setError('');
    setResult(null);
  };

  const handlePdfSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith('.zip') && !name.endsWith('.rar') && !name.endsWith('.pdf')) {
      setError('Selecciona un archivo ZIP o RAR con los PDFs dentro');
      return;
    }

    setPdfFile(file);
    setError('');
    setResult(null);
  };

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB per chunk

  const uploadFileInChunks = async (file, jobId, fileType) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      
      const form = new FormData();
      form.append('job_id', jobId);
      form.append('chunk_index', i.toString());
      form.append('total_chunks', totalChunks.toString());
      form.append('filename', file.name);
      form.append('file_type', fileType);
      form.append('chunk', blob, `chunk_${i}`);
      
      await axios.post(`${API}/invoice-classifier/upload-chunk`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 120000
      });
    }
  };

  const handleProcess = async () => {
    if (!excelFile) {
      setError('Selecciona el archivo Excel primero');
      return;
    }

    if (!pdfFile) {
      setError('Selecciona el ZIP con los PDFs');
      return;
    }

    if (!token) {
      setError('Sesión no válida');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setUploadProgress(0);
    setProcessingStage('uploading');

    try {
      // Generate job ID client-side
      const jobId = Math.random().toString(36).substring(2, 10);
      setCurrentJobId(jobId);

      // Upload Excel in chunks
      const excelChunks = Math.ceil(excelFile.size / CHUNK_SIZE);
      await uploadFileInChunks(excelFile, jobId, 'excel');
      setUploadProgress(10);

      // Upload archive in chunks with progress
      const archiveChunks = Math.ceil(pdfFile.size / CHUNK_SIZE);
      for (let i = 0; i < archiveChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, pdfFile.size);
        const blob = pdfFile.slice(start, end);

        const form = new FormData();
        form.append('job_id', jobId);
        form.append('chunk_index', i.toString());
        form.append('total_chunks', archiveChunks.toString());
        form.append('filename', pdfFile.name);
        form.append('file_type', 'archive');
        form.append('chunk', blob, `chunk_${i}`);

        await axios.post(`${API}/invoice-classifier/upload-chunk`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          timeout: 120000
        });

        const pct = 10 + Math.round(((i + 1) / archiveChunks) * 85);
        setUploadProgress(pct);
      }

      setUploadProgress(100);
      setProcessingStage('processing');

      // Start processing
      await axios.post(`${API}/invoice-classifier/start-chunked/${jobId}`, null, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 30000
      });

      // Start polling
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(jobId);
      }, 2000);

    } catch (err) {
      console.error('Error:', err);
      setProcessingStage('');
      setLoading(false);
      setError(err.response?.data?.detail || 'Error al subir');
    }
  };

  const handleDownload = async (jobId = null) => {
    const downloadJobId = jobId || result?.jobId;
    if (!downloadJobId) return;

    try {
      const response = await axios.get(
        `${API}/invoice-classifier/job/${downloadJobId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 300000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facturas_clasificadas_${downloadJobId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (err) {
      console.error('Error downloading:', err);
      toast.error('Error al descargar');
    }
  };

  const handleReset = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setExcelFile(null);
    setPdfFile(null);
    setResult(null);
    setError('');
    setUploadProgress(0);
    setProcessingStage('');
    setCurrentJobId(null);
    setLoading(false);
    if (excelInputRef.current) excelInputRef.current.value = '';
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  const handleDeleteJob = async (jobId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/invoice-classifier/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobHistory();
      toast.success('Job eliminado');
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Clasificador de Facturas PDF</h2>
        <p className="text-muted-foreground">
          Sube un Excel con los datos de referencia y los PDFs de facturas para clasificarlos automáticamente
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6">
            <div className="space-y-3">
              {/* Main stats */}
              <div className="flex items-center gap-2 text-green-700 font-semibold text-lg">
                <CheckCircle className="h-5 w-5" />
                Clasificación completada
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 text-center border" data-testid="stat-pdfs-uploaded">
                  <p className="text-2xl font-bold text-blue-600">{result.totalPdfs}</p>
                  <p className="text-xs text-muted-foreground">PDFs subidos</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border" data-testid="stat-pdfs-matched">
                  <p className="text-2xl font-bold text-green-600">{result.matched}</p>
                  <p className="text-xs text-muted-foreground">En el ZIP</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border" data-testid="stat-pdfs-unmatched">
                  <p className="text-2xl font-bold text-yellow-600">{result.unmatched}</p>
                  <p className="text-xs text-muted-foreground">Sin clasificar</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border" data-testid="stat-excel-rows">
                  <p className="text-2xl font-bold text-gray-600">{result.activeExcelRows}</p>
                  <p className="text-xs text-muted-foreground">Filas Excel activas</p>
                </div>
              </div>

              {/* Green rows info */}
              {result.greenSkipped > 0 && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-sm"></span>
                  {result.greenSkipped} filas verdes saltadas (ya archivadas) de {result.totalExcelRows} totales
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Excel Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              1. Excel de Referencias
            </CardTitle>
            <CardDescription>
              Columnas: A-NºOrden, B-Referencia, C-NºFact, D-Fecha, E-Concepto, F-NIF, G-Expedidor, H-Base, I-%IVA, J-Cuota, K-Ret, L-Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              ref={excelInputRef}
              onChange={handleExcelSelect}
              accept=".xlsx,.xls"
              className="hidden"
            />
            
            <div
              onClick={() => !loading && excelInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                loading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-green-500/50 hover:bg-green-50/50'
              } ${excelFile ? 'border-green-500 bg-green-50' : ''}`}
            >
              <FileSpreadsheet className={`h-10 w-10 mx-auto mb-2 ${excelFile ? 'text-green-600' : 'text-muted-foreground'}`} />
              {excelFile ? (
                <div>
                  <p className="font-medium text-green-700">{excelFile.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(excelFile.size)}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Click para seleccionar Excel</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* PDF/ZIP Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Files className="h-5 w-5 text-red-600" />
              2. Facturas (ZIP)
            </CardTitle>
            <CardDescription>
              Sube un ZIP con todos los PDFs de facturas dentro. Sin límite de cantidad.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              ref={pdfInputRef}
              onChange={handlePdfSelect}
              accept=".zip,.rar,.pdf"
              className="hidden"
              data-testid="pdf-zip-input"
            />
            
            <div
              onClick={() => !loading && pdfInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                loading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-red-500/50 hover:bg-red-50/50'
              } ${pdfFile ? 'border-red-500 bg-red-50' : ''}`}
            >
              <Files className={`h-10 w-10 mx-auto mb-2 ${pdfFile ? 'text-red-600' : 'text-muted-foreground'}`} />
              {pdfFile ? (
                <div>
                  <p className="font-medium text-red-700">{pdfFile.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(pdfFile.size)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-muted-foreground">Click para seleccionar ZIP/RAR con PDFs</p>
                  <p className="text-xs text-muted-foreground mt-1">Acepta .zip y .rar — sin límite de cantidad</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {processingStage === 'uploading' && 'Subiendo archivos...'}
                  {processingStage === 'extracting' && 'Extrayendo PDFs del archivo...'}
                  {processingStage === 'processing' && 'Clasificando facturas...'}
                  {processingStage === 'done' && '¡Completado!'}
                </span>
                {processingStage === 'uploading' && (
                  <span className="font-medium">{uploadProgress}%</span>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    (processingStage === 'processing' || processingStage === 'extracting') ? 'bg-yellow-500 animate-pulse w-full' : 'bg-primary'
                  }`}
                  style={{ width: (processingStage === 'processing' || processingStage === 'extracting') ? '100%' : `${uploadProgress}%` }}
                ></div>
              </div>
              {processingStage === 'uploading' && (
                <div className="flex items-center gap-2 text-blue-600 text-sm">
                  <Upload className="h-4 w-4 animate-pulse" />
                  <span>Subiendo archivo ({uploadProgress}%)...</span>
                </div>
              )}
              {processingStage === 'extracting' && (
                <div className="flex items-center gap-2 text-orange-600 text-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Extrayendo PDFs del RAR/ZIP... esto puede tardar unos segundos</span>
                </div>
              )}
              {processingStage === 'processing' && (
                <div className="flex items-center gap-2 text-yellow-600 text-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Extrayendo datos de los PDFs y buscando coincidencias...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleProcess}
          disabled={!excelFile || !pdfFile || loading}
          className="flex-1"
          size="lg"
          data-testid="process-invoices-button"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <FolderOpen className="h-4 w-4 mr-2" />
              Clasificar Facturas
            </>
          )}
        </Button>

        {result && (
          <Button onClick={() => handleDownload()} size="lg" variant="default" className="bg-green-600 hover:bg-green-700">
            <Download className="h-4 w-4 mr-2" />
            Descargar ZIP
          </Button>
        )}

        {(excelFile || pdfFile) && (
          <Button variant="outline" onClick={handleReset} size="lg">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Results Detail */}
      {result && result.matches && result.matches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalle de Clasificación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {result.matches.map((match, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    match.status === 'matched' ? 'bg-green-50 border border-green-200' :
                    match.status === 'unmatched' ? 'bg-yellow-50 border border-yellow-200' :
                    'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {match.status === 'matched' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {match.status === 'unmatched' && <AlertCircle className="h-4 w-4 text-yellow-600" />}
                    {match.status === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
                    <div>
                      <p className="font-medium text-sm">{match.pdf}</p>
                      {match.status === 'matched' && (
                        <p className="text-xs text-green-700">
                          → {match.new_filename} ({match.matched_fields?.join(', ')})
                        </p>
                      )}
                      {match.status === 'unmatched' && (
                        <p className="text-xs text-yellow-700">
                          Sin coincidencia encontrada
                        </p>
                      )}
                      {match.status === 'error' && (
                        <p className="text-xs text-red-700">{match.error}</p>
                      )}
                    </div>
                  </div>
                  {match.score && (
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                      Score: {match.score}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job History */}
      {jobHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Historial
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobHistory.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {job.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {job.status === 'processing' && <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />}
                    {job.status === 'error' && <AlertCircle className="h-4 w-4 text-red-600" />}
                    {job.status === 'pending' && <Clock className="h-4 w-4 text-gray-400" />}
                    <div>
                      <p className="text-sm font-medium">
                        {job.pdf_count} PDFs subidos → {job.matched || 0} en ZIP
                        {(job.green_skipped || 0) > 0 && (
                          <span className="text-emerald-600 ml-1">({job.green_skipped} verdes saltadas)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(job.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {job.status === 'completed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(job.id)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteJob(job.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Instrucciones</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>1.</strong> Sube el Excel con las columnas en orden: NºOrden, Referencia, NºFact, Fecha, Concepto, NIF, Expedidor, Base, %IVA, Cuota, Retención, Total</p>
          <p><strong>2.</strong> Mete todos los PDFs de facturas dentro de un archivo .zip y súbelo</p>
          <p><strong>3.</strong> El sistema extraerá los PDFs del ZIP, leerá los datos de cada uno y buscará coincidencias en el Excel</p>
          <p><strong>4.</strong> Descarga el ZIP resultado con los PDFs renombrados según la columna Referencia</p>
          <p className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-green-500 rounded-sm flex-shrink-0"></span>
            <span><strong>Filas verdes:</strong> Las filas pintadas de verde en el Excel se saltan automáticamente (ya están archivadas)</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceClassifierTab;
